import { supabase, SUPABASE_PAGE_SIZE } from '../supabase';
import { useQuery, useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SpamShift } from '@shiftly/shared';

export interface SpamShiftFilter {
  field: keyof SpamShift | string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'not-in' | 'is';
  value: any;
}

export interface SpamShiftSort {
  field: keyof SpamShift | string;
  direction: 'asc' | 'desc';
}

export type GetSpamShiftsParams = {
  filters?: SpamShiftFilter[];
  sort?: SpamShiftSort[];
  enabled?: boolean;
  limit?: number;
};

export function convertDateFields(data: any): SpamShift {
  return {
    ...data,
    start_date_time: DateTime.fromISO(data.start_date_time, { zone: 'UTC' }),
    end_date_time: DateTime.fromISO(data.end_date_time, { zone: 'UTC' }),
    created_at: DateTime.fromISO(data.created_at, { zone: 'UTC' }),
    updated_at: DateTime.fromISO(data.updated_at, { zone: 'UTC' })
  }
}

class SpamShiftService {
  async getSpamShifts({ filters = [], sort = [], limit }: GetSpamShiftsParams): Promise<SpamShift[]> {
    try {
      // Standard single query path
      if (limit) {
        let query = supabase.from('spam_shifts').select('*');

        // Apply filters
        filters.forEach(filter => {          
          switch (filter.operator) {
            case 'eq':
              query = query.eq(filter.field, filter.value);
              break;
            case 'neq':
              query = query.neq(filter.field, filter.value);
              break;
            case 'gt':
              query = query.gt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
              break;
            case 'gte':
              query = query.gte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
              break;
            case 'lt':
              query = query.lt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
              break;
            case 'lte':
              query = query.lte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
              break;
            case 'in':
              query = query.in(filter.field, filter.value);
              break;
            case 'not-in':
              query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
              break;
            case 'is':
              query = query.is(filter.field, filter.value);
              break;
          }
        });

        // Apply sorting with stable secondary sort
        if (sort.length > 0) {
          sort.forEach(sortOption => {
            query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
          });
          // Add stable secondary sort if not already sorting by id
          if (!sort.some(s => s.field === 'id')) {
            query = query.order('id', { ascending: true });
          }
        } else {
          query = query.order('start_date_time', { ascending: true }).order('id', { ascending: true });
        }

        query = query.limit(limit);

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching spam shifts:', error);
          throw error;
        }

        return data.map((shift: SpamShift) => convertDateFields(shift));
      } else {
        // Unlimited query - use pagination for performance
        const allResults: SpamShift[] = [];
        let hasMore = true;
        let offset = 0;

        while (hasMore) {
          let query = supabase.from('spam_shifts').select('*');

          // Apply filters
          filters.forEach(filter => {
            switch (filter.operator) {
              case 'eq':
                query = query.eq(filter.field, filter.value);
                break;
              case 'neq':
                query = query.neq(filter.field, filter.value);
                break;
              case 'gt':
                query = query.gt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'gte':
                query = query.gte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'lt':
                query = query.lt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'lte':
                query = query.lte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
                break;
              case 'in':
                query = query.in(filter.field, filter.value);
                break;
              case 'not-in':
                query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
                break;
              case 'is':
                query = query.is(filter.field, filter.value);
                break;
            }
          });

          // Apply sorting with stable secondary sort
          if (sort.length > 0) {
            sort.forEach(sortOption => {
              query = query.order(sortOption.field, { ascending: sortOption.direction === 'asc' });
            });
            // Add stable secondary sort if not already sorting by id
            if (!sort.some(s => s.field === 'id')) {
              query = query.order('id', { ascending: true });
            }
          } else {
            query = query.order('start_date_time', { ascending: true }).order('id', { ascending: true });
          }

          // Add pagination
          query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

          const { data, error } = await query;

          if (error) {
            console.error('Error fetching spam shifts page:', error);
            throw error;
          }

          const shifts = (data || []);
          allResults.push(...shifts);

          // Check if we got fewer results than requested (end of data)
          hasMore = shifts.length === SUPABASE_PAGE_SIZE;
          offset += SUPABASE_PAGE_SIZE;
        }

        return allResults.map((shift: SpamShift) => convertDateFields(shift));
      }
    } catch (error) {
      console.error('Error in getSpamShifts:', error);
      return [];
    }
  }

  async createSpamShift(shift: Omit<SpamShift, 'id'>): Promise<string> {
    const now = DateTime.utc();
    const record = {
      ...shift,
      created_at: now.toISO(),
      updated_at: now.toISO()
    };

    const { data, error } = await supabase
      .from('spam_shifts')
      .insert(record)
      .select('id')
      .single();

    if (error) {
      throw new Error(`Failed to create spam shift: ${error.message}`);
    }

    return data.id;
  }

  async updateSpamShift(shiftId: string, updates: Partial<SpamShift>): Promise<void> {
    const record = {
      ...updates,
      updated_at: DateTime.utc().toISO()
    };

    const { error } = await supabase
      .from('spam_shifts')
      .update(record)
      .eq('id', shiftId);

    if (error) {
      throw new Error(`Failed to update spam shift: ${error.message}`);
    }
  }

  async deleteSpamShift(shiftId: string): Promise<void> {
    const { error } = await supabase
      .from('spam_shifts')
      .delete()
      .eq('id', shiftId);

    if (error) {
      throw new Error(`Failed to delete spam shift: ${error.message}`);
    }
  }

  async getSpamShiftsByGroup(group: string, startDate?: DateTime, endDate?: DateTime): Promise<SpamShift[]> {
    const filters: SpamShiftFilter[] = [
      { field: 'group', operator: 'eq', value: group }
    ];
    
    if (startDate) {
      filters.push({ field: 'start_date_time', operator: 'gte', value: startDate.toISO() });
    }
    
    if (endDate) {
      filters.push({ field: 'start_date_time', operator: 'lte', value: endDate.toISO() });
    }
    
    return this.getSpamShifts({ filters });
  }

  async getUserSpamShifts(user_id: string, startDate?: DateTime, endDate?: DateTime): Promise<SpamShift[]> {
    const filters: SpamShiftFilter[] = [
      { field: 'user_id', operator: 'eq', value: user_id }
    ];
    
    if (startDate) {
      filters.push({ field: 'start_date_time', operator: 'gte', value: startDate.toISO() });
    }
    
    if (endDate) {
      filters.push({ field: 'start_date_time', operator: 'lte', value: endDate.toISO() });
    }
    
    return this.getSpamShifts({ filters });
  }

  async deleteSpamShiftsByGroup(group: string, startDate?: DateTime, endDate?: DateTime): Promise<void> {
    const shifts = await this.getSpamShiftsByGroup(group, startDate, endDate);
    
    const deletePromises = shifts.map(shift => this.deleteSpamShift(shift.id));
    await Promise.all(deletePromises);
  }
}

const instance = new SpamShiftService();

export const useSpamShifts = (params: GetSpamShiftsParams = { filters: [], sort: [] }) => {
  return useQuery({
    queryKey: ['supabase-spam-shifts', params],
    queryFn: () => instance.getSpamShifts(params),
    enabled: params.enabled !== false
  });
};

export const useCreateSpamShift = () => {
  return useMutation({
    mutationFn: (shift: Omit<SpamShift, 'id'>) => instance.createSpamShift(shift),
  });
};

export const useUpdateSpamShift = () => {
  return useMutation({
    mutationFn: ({ shift_id, updates }: { shift_id: string; updates: Partial<SpamShift> }) => 
      instance.updateSpamShift(shift_id, updates),
  });
};

export const useDeleteSpamShift = () => {
  return useMutation({
    mutationFn: (shiftId: string) => instance.deleteSpamShift(shiftId),
  });
};

export const useSpamShiftsByGroup = (group: string, startDate?: DateTime, endDate?: DateTime) => {
  const filters: SpamShiftFilter[] = [
    { field: 'group', operator: 'eq', value: group }
  ];
  
  if (startDate) {
    filters.push({ field: 'start_date_time', operator: 'gte', value: startDate.toISO() });
  }
  
  if (endDate) {
    filters.push({ field: 'start_date_time', operator: 'lte', value: endDate.toISO() });
  }
  
  const params: GetSpamShiftsParams = {
    filters,
    enabled: !!group
  };
  
  return useQuery({
    queryKey: ['supabase-spam-shifts', params],
    queryFn: () => instance.getSpamShifts(params),
    enabled: !!group
  });
};

export const useUserSpamShifts = (user_id: string | undefined, startDate?: DateTime, endDate?: DateTime) => {
  const filters: SpamShiftFilter[] = [];
  
  if (user_id) {
    filters.push({ field: 'user_id', operator: 'eq', value: user_id });
  }
  
  if (startDate) {
    filters.push({ field: 'start_date_time', operator: 'gte', value: startDate.toISO() });
  }
  
  if (endDate) {
    filters.push({ field: 'start_date_time', operator: 'lte', value: endDate.toISO() });
  }
  
  const params: GetSpamShiftsParams = {
    filters,
    enabled: !!user_id
  };
  
  return useQuery({
    queryKey: ['supabase-spam-shifts', params],
    queryFn: () => instance.getSpamShifts(params),
    enabled: !!user_id
  });
};

export const useDeleteSpamShiftsByGroup = () => {
  return useMutation({
    mutationFn: ({ group, startDate, endDate }: { group: string; startDate?: DateTime; endDate?: DateTime }) =>
      instance.deleteSpamShiftsByGroup(group, startDate, endDate),
  });
};

export default instance;